import { graphQlClient } from '@config/graphqlClient';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import { SnackType } from '@models/common.model';
import {
  createStepExecutionRequest,
  getCurrentIterationNumberRequest,
  getMyPendingExecutionsCountRequest,
  getMyPendingExecutionsRequest,
  getSubstepExecutionsRequest,
  goBackToPreviousStepRequest,
  initializeSwitchSubstepsRequest,
  ProcessExecutionStatus,
  startNewIterationRequest,
  startProcessExecutionRequest,
  StepExecution,
  StepExecutionStatus,
  updateStepExecutionRequest
} from '@models/processExecution.model';
import { ConfirmationType, Step, StepType } from '@models/step.model';
import { Process } from '@models/process.model';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { setPendingExecutionsCount } from '@redux/reducers/badges.reducer';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface ProcessHeaderRef {
  fetchLastExecution: () => Promise<void>;
}

interface UseProcessExecutionProps {
  currentProcess: Process | undefined;
  processId: string;
  stepsRefs: React.MutableRefObject<{ [key: string]: any | null }>;
  processHeaderRef: React.RefObject<ProcessHeaderRef>;
  localeProcesses: Record<string, string>;
  localeActionMessages: Record<string, string>;
}

interface UseProcessExecutionReturn {
  // State
  isExecuting: boolean;
  processExecutionId: string | null;
  stepExecutions: StepExecution[];
  currentStepIndex: number;
  showStatusModal: boolean;
  isGoingBack: boolean;
  substepExecutions: Map<string, StepExecution[]>;
  currentSubstepIndices: Map<string, number>;
  repeatIterations: Map<string, number>;
  completedIterations: Map<string, number[]>;
  showIterationCompleteFor: string | null;
  showContinueExecutionModal: boolean;
  pendingExecutionStartDate: string | null;
  pendingExecutionId: string | null;
  showAssignerNoteModal: boolean;
  assignerNoteContent: string | null;
  noteModalNextAction: 'start' | 'showContinueModal';

  // State setters (for modals controlled externally)
  setShowStatusModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowContinueExecutionModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowAssignerNoteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setAssignerNoteContent: React.Dispatch<React.SetStateAction<string | null>>;
  setPendingExecutionStartDate: React.Dispatch<React.SetStateAction<string | null>>;
  setPendingExecutionId: React.Dispatch<React.SetStateAction<string | null>>;
  setNoteModalNextAction: React.Dispatch<React.SetStateAction<'start' | 'showContinueModal'>>;

  // Functions
  isSubstep: (step: Step, allSteps: Step[]) => boolean;
  checkAndShowExecutionConfirm: () => Promise<void>;
  handleStartExecution: (forceNew?: boolean) => Promise<void>;
  handleMarkStep: (stepIndex: number, status: StepExecutionStatus, notes?: string, confirmationResponse?: string) => Promise<void>;
  handleCompleteExecution: (finalStatus: ProcessExecutionStatus) => Promise<void>;
  handleCancelExecution: () => Promise<void>;
  handleGoBack: () => Promise<void>;
  getSubstepsForParent: (parentStepId: string) => StepExecution[];
  isContainerStep: (step: Step) => boolean;
  initializeSwitchExecution: (parentStepId: string, sequenceOrder: number) => Promise<void>;
  startRepeatIteration: (parentStepId: string, sequenceOrder: number, iterationNumber: number) => Promise<void>;
  handleMarkSubstep: (parentStepId: string, substepIndex: number, status: StepExecutionStatus, notes?: string, confirmationResponse?: string) => Promise<void>;
  handleRepeatAddAnother: (parentStepId: string) => Promise<void>;
  handleRepeatDone: (parentStepId: string) => Promise<void>;
}

export const useProcessExecution = ({
  currentProcess,
  processId,
  stepsRefs,
  processHeaderRef,
  localeProcesses,
  localeActionMessages,
}: UseProcessExecutionProps): UseProcessExecutionReturn => {
  const dispatch = useDispatch();

  // Execution state
  const [isExecuting, setIsExecuting] = useState(false);
  const [processExecutionId, setProcessExecutionId] = useState<string | null>(null);
  const [stepExecutions, setStepExecutions] = useState<StepExecution[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [isGoingBack, setIsGoingBack] = useState(false);

  // Substep execution state
  const [substepExecutions, setSubstepExecutions] = useState<Map<string, StepExecution[]>>(new Map());
  const [currentSubstepIndices, setCurrentSubstepIndices] = useState<Map<string, number>>(new Map());
  const [repeatIterations, setRepeatIterations] = useState<Map<string, number>>(new Map());
  const [completedIterations, setCompletedIterations] = useState<Map<string, number[]>>(new Map());
  const [showIterationCompleteFor, setShowIterationCompleteFor] = useState<string | null>(null);

  // Continue execution modal state
  const [showContinueExecutionModal, setShowContinueExecutionModal] = useState(false);
  const [pendingExecutionStartDate, setPendingExecutionStartDate] = useState<string | null>(null);
  const [pendingExecutionId, setPendingExecutionId] = useState<string | null>(null);

  // Assigner note modal state
  const [showAssignerNoteModal, setShowAssignerNoteModal] = useState(false);
  const [assignerNoteContent, setAssignerNoteContent] = useState<string | null>(null);
  const [noteModalNextAction, setNoteModalNextAction] = useState<'start' | 'showContinueModal'>('start');

  // Helper functions
  const isSubstep = useCallback((step: Step, allSteps: Step[]): boolean => {
    return allSteps.some((s) => {
      if (s.stepData?.stepsIds && typeof s.stepData.stepsIds === 'string') {
        return s.stepData.stepsIds.split(',').filter((id: string) => id && id !== 'undefined').includes(step.stepId);
      }
      return false;
    });
  }, []);

  const isContainerStep = useCallback((step: Step): boolean => {
    return step.stepType === StepType.SWITCH || step.stepType === StepType.REPEAT;
  }, []);

  const isValidUUID = useCallback((id: string | null | undefined): boolean => {
    if (!id || id === 'undefined' || id === 'null') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  }, []);

  const sortStepExecutions = useCallback((steps: StepExecution[]): StepExecution[] => {
    return [...steps].sort((a, b) => (a?.sequenceOrder ?? 0) - (b?.sequenceOrder ?? 0));
  }, []);

  const getSubstepsForParent = useCallback((parentStepId: string): StepExecution[] => {
    return substepExecutions.get(parentStepId) || [];
  }, [substepExecutions]);

  // Reset execution state helper
  const resetExecutionState = useCallback(() => {
    // Set isExecuting to false FIRST to prevent auto-scroll useEffect from triggering
    setIsExecuting(false);
    setStepExecutions([]);
    setCurrentStepIndex(0);
    setProcessExecutionId(null);
    setShowStatusModal(false);
    setSubstepExecutions(new Map());
    setCurrentSubstepIndices(new Map());
    setRepeatIterations(new Map());
    setCompletedIterations(new Map());
    setShowIterationCompleteFor(null);
    setAssignerNoteContent(null);
  }, []);

  // Refresh pending executions count
  const refreshPendingExecutionsCount = useCallback(async () => {
    try {
      const countData: getMyPendingExecutionsCountRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyPendingExecutionsCount,
      );
      if (countData?.getMyPendingExecutionsCount !== undefined) {
        dispatch(setPendingExecutionsCount(countData.getMyPendingExecutionsCount));
      }
    } catch (countError) {
      console.error('Error refreshing pending executions count:', countError);
    }
  }, [dispatch]);

  // Check if all steps (including substeps for container steps) are completed
  const checkAllStepsCompleted = useCallback((updatedStepId?: string, updatedSubsteps?: StepExecution[]): boolean => {
    const filteredSteps = currentProcess?.steps
      ?.filter(step => !isSubstep(step, currentProcess.steps || []))
      .sort((a, b) => a.order - b.order) || [];

    for (const step of filteredSteps) {
      if (isContainerStep(step)) {
        const substeps = (step.stepId === updatedStepId && updatedSubsteps)
          ? updatedSubsteps
          : getSubstepsForParent(step.stepId);
        if (substeps.length === 0) return false;
        const allSubstepsComplete = substeps.every(s =>
          s.status === StepExecutionStatus.COMPLETED ||
          s.status === StepExecutionStatus.SKIPPED ||
          s.status === StepExecutionStatus.FAILED
        );
        if (!allSubstepsComplete) return false;
      } else {
        const stepExec = stepExecutions.find(se => se.stepId === step.stepId);
        if (!stepExec) return false;
        if (stepExec.status !== StepExecutionStatus.COMPLETED &&
            stepExec.status !== StepExecutionStatus.SKIPPED &&
            stepExec.status !== StepExecutionStatus.FAILED) {
          return false;
        }
      }
    }
    return true;
  }, [currentProcess?.steps, isSubstep, isContainerStep, getSubstepsForParent, stepExecutions]);

  // Complete execution
  const handleCompleteExecution = useCallback(async (finalStatus: ProcessExecutionStatus) => {
    if (!processExecutionId) return;

    try {
      await graphQlClient.request(ProcessExecutionGraphQL.mutations.completeProcessExecution, {
        id: processExecutionId,
        status: finalStatus,
      });

      const message =
        finalStatus === ProcessExecutionStatus.COMPLETED
          ? localeProcesses['executionCompleted']
          : finalStatus === ProcessExecutionStatus.FAILED
            ? localeProcesses['executionFailed']
            : localeProcesses['executionCancelled'];

      dispatch(appendActionMessage({
        message: message,
        type: finalStatus === ProcessExecutionStatus.COMPLETED ? SnackType.SUCCESS : SnackType.INFO,
      }));

      resetExecutionState();

      if (processHeaderRef.current) {
        await processHeaderRef.current.fetchLastExecution();
      }

      await refreshPendingExecutionsCount();
    } catch (error) {
      console.error('Error completing execution:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [processExecutionId, localeProcesses, localeActionMessages, dispatch, resetExecutionState, processHeaderRef, refreshPendingExecutionsCount]);

  // Move to the next main step after completing a container step's substeps
  const moveToNextMainStep = useCallback(async (completedStepId: string, updatedSubstepsForStep?: StepExecution[]) => {
    setCurrentSubstepIndices(prev => {
      const newMap = new Map(prev);
      newMap.delete(completedStepId);
      return newMap;
    });
    setShowIterationCompleteFor(null);

    const filteredSteps = currentProcess?.steps
      ?.filter(step => !isSubstep(step, currentProcess.steps || []))
      .sort((a, b) => a.order - b.order) || [];

    const parentIndex = filteredSteps.findIndex(s => s.stepId === completedStepId);
    if (parentIndex >= 0 && parentIndex < filteredSteps.length - 1) {
      setCurrentStepIndex(parentIndex + 1);
    } else {
      const allMainStepsCompleted = checkAllStepsCompleted(completedStepId, updatedSubstepsForStep);
      if (allMainStepsCompleted) {
        const hasFailedSteps = stepExecutions.some((step) => step.status === StepExecutionStatus.FAILED);
        const hasSkippedSteps = stepExecutions.some((step) => step.status === StepExecutionStatus.SKIPPED);
        if (hasFailedSteps || hasSkippedSteps) {
          setShowStatusModal(true);
        } else {
          await handleCompleteExecution(ProcessExecutionStatus.COMPLETED);
        }
      }
    }
  }, [currentProcess?.steps, isSubstep, checkAllStepsCompleted, stepExecutions, handleCompleteExecution]);

  // Initialize switch execution
  const initializeSwitchExecution = useCallback(async (parentStepId: string, sequenceOrder: number) => {
    if (!isValidUUID(processExecutionId) || !isValidUUID(parentStepId)) return;
    try {
      const response: initializeSwitchSubstepsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.initializeSwitchSubsteps,
        { processExecutionId, parentStepId, sequenceOrder }
      );

      const substeps = response.initializeSwitchSubsteps || [];

      if (substeps.length === 0) {
        moveToNextMainStep(parentStepId);
        return;
      }

      setSubstepExecutions(prev =>
        new Map(prev).set(parentStepId, substeps)
      );
      setCurrentSubstepIndices(prev => new Map(prev).set(parentStepId, 0));
    } catch (error) {
      console.error('Error initializing SWITCH substeps:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [processExecutionId, isValidUUID, moveToNextMainStep, dispatch, localeActionMessages]);

  // Start repeat iteration
  const startRepeatIteration = useCallback(async (parentStepId: string, sequenceOrder: number, iterationNumber: number) => {
    if (!isValidUUID(processExecutionId) || !isValidUUID(parentStepId)) return;
    try {
      const response: startNewIterationRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.startNewIteration,
        { processExecutionId, parentStepId, sequenceOrder }
      );

      const newSubsteps = response.startNewIteration || [];

      if (newSubsteps.length === 0) {
        moveToNextMainStep(parentStepId);
        return;
      }

      setSubstepExecutions(prev => {
        const existing = prev.get(parentStepId) || [];
        return new Map(prev).set(parentStepId, [...existing, ...newSubsteps]);
      });

      setRepeatIterations(prev => new Map(prev).set(parentStepId, iterationNumber));
      setCurrentSubstepIndices(prev => new Map(prev).set(parentStepId, 0));
      setShowIterationCompleteFor(null);
    } catch (error) {
      console.error('Error starting REPEAT iteration:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [processExecutionId, isValidUUID, moveToNextMainStep, dispatch, localeActionMessages]);

  // Helper to check if a step is in-progress (not yet saved)
  const isInProgressStep = (stepId: string | undefined): boolean => {
    return stepId?.startsWith('in-progress-') || false;
  };

  // Internal start execution (used by checkAndShowExecutionConfirm)
  const handleStartExecutionInternal = useCallback(async (forceNew: boolean = false) => {
    // Filter out in-progress steps (not yet saved) when checking if there are steps to execute
    const savedSteps = currentProcess?.steps?.filter(step => !isInProgressStep(step?.stepId)) || [];
    if (!currentProcess || !currentProcess.steps || savedSteps.length === 0) {
      dispatch(appendActionMessage({
        message: localeActionMessages['noStepsToExecute'] || 'No steps to execute',
        type: SnackType.ERROR,
      }));
      return;
    }

    // Check for empty container steps (SWITCH/REPEAT with no substeps)
    const emptyContainerSteps = savedSteps.filter(step => {
      if (step.stepType === StepType.SWITCH || step.stepType === StepType.REPEAT) {
        const childStepIds = step.stepData?.stepsIds
          ?.split(',')
          .filter((id: string) => id && id.trim() && id !== 'undefined') || [];
        return childStepIds.length === 0;
      }
      return false;
    });

    if (emptyContainerSteps.length > 0) {
      const stepNames = emptyContainerSteps.map(s => s.name).join(', ');
      dispatch(appendActionMessage({
        message: localeProcesses['emptyContainerStepsWarning']?.replace('{steps}', stepNames)
          || `The following steps have no substeps and cannot be completed: ${stepNames}`,
        type: SnackType.WARNING,
      }));
      return;
    }

    // At this point, currentProcess and currentProcess.steps are guaranteed to be defined
    const process = currentProcess;
    const steps = currentProcess.steps;

    try {
      if (forceNew && pendingExecutionId) {
        await graphQlClient.request(ProcessExecutionGraphQL.mutations.completeProcessExecution, {
          id: pendingExecutionId,
          status: ProcessExecutionStatus.CANCELLED,
        });
        setPendingExecutionId(null);
      }

      const response: startProcessExecutionRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.startProcessExecution,
        {
          input: {
            processId: processId,
            processSnapshot: {
              name: process.name,
              goal: process.goal,
              category: process.category,
            },
            forceNew,
          },
        },
      );

      const executionId = response.startProcessExecution.processExecutionId;
      const resumedFrom = response.startProcessExecution.resumedFrom;
      setProcessExecutionId(executionId);

      // Set assigner note from response (backup in case it wasn't set earlier)
      if (response.startProcessExecution.assignerNote) {
        setAssignerNoteContent(response.startProcessExecution.assignerNote);
      }

      if (resumedFrom === 'IN_PROGRESS') {
        dispatch(appendActionMessage({
          message: localeProcesses['continuingPreviousExecution'],
          type: SnackType.INFO,
        }));
      } else if (resumedFrom === 'ASSIGNED') {
        dispatch(appendActionMessage({
          message: localeProcesses['startingAssignedExecution'],
          type: SnackType.INFO,
        }));
      }

      if (resumedFrom && response.startProcessExecution.stepExecutions?.length) {
        const existingSteps = sortStepExecutions(response.startProcessExecution.stepExecutions);
        setStepExecutions(existingSteps);

        const topLevelSteps = steps
          .filter(step => !isSubstep(step, steps))
          .sort((a, b) => a.order - b.order);

        let resumeStepIndex = 0;
        for (let i = 0; i < topLevelSteps.length; i++) {
          const step = topLevelSteps[i];
          if (step.stepType === StepType.SWITCH || step.stepType === StepType.REPEAT) {
            const childStepIds = step.stepData?.stepsIds
              ?.split(',')
              .filter((id: string) => id && id.trim() && id !== 'undefined') || [];

            if (childStepIds.length === 0) continue;

            try {
              const substepsResponse: getSubstepExecutionsRequest = await graphQlClient.request(
                ProcessExecutionGraphQL.queries.getSubstepExecutions,
                { processExecutionId: executionId, parentStepId: step.stepId }
              );
              const substeps = substepsResponse.getSubstepExecutions || [];

              if (substeps.length > 0) {
                setSubstepExecutions(prev => new Map(prev).set(step.stepId, substeps));

                if (step.stepType === StepType.REPEAT) {
                  const iterResponse: getCurrentIterationNumberRequest = await graphQlClient.request(
                    ProcessExecutionGraphQL.queries.getCurrentIterationNumber,
                    { processExecutionId: executionId, parentStepId: step.stepId }
                  );
                  const currentIter = iterResponse.getCurrentIterationNumber || 1;
                  setRepeatIterations(prev => new Map(prev).set(step.stepId, currentIter));

                  const completedIters: number[] = [];
                  for (let iter = 1; iter < currentIter; iter++) {
                    completedIters.push(iter);
                  }
                  if (completedIters.length > 0) {
                    setCompletedIterations(prev => new Map(prev).set(step.stepId, completedIters));
                  }

                  const currentIterSubsteps = substeps.filter(s => s.iterationNumber === currentIter);
                  const firstPendingSubstepIdx = currentIterSubsteps.findIndex(
                    s => s.status === StepExecutionStatus.PENDING || s.status === StepExecutionStatus.IN_PROGRESS
                  );
                  if (firstPendingSubstepIdx >= 0) {
                    setCurrentSubstepIndices(prev => new Map(prev).set(step.stepId, firstPendingSubstepIdx));
                    resumeStepIndex = i;
                    break;
                  }
                  const allCurrentIterComplete = currentIterSubsteps.every(
                    s => s.status === StepExecutionStatus.COMPLETED || s.status === StepExecutionStatus.SKIPPED || s.status === StepExecutionStatus.FAILED
                  );
                  if (allCurrentIterComplete && currentIterSubsteps.length > 0) {
                    setShowIterationCompleteFor(step.stepId);
                    resumeStepIndex = i;
                    break;
                  }
                } else {
                  const firstPendingSubstepIdx = substeps.findIndex(
                    s => s.status === StepExecutionStatus.PENDING || s.status === StepExecutionStatus.IN_PROGRESS
                  );
                  if (firstPendingSubstepIdx >= 0) {
                    setCurrentSubstepIndices(prev => new Map(prev).set(step.stepId, firstPendingSubstepIdx));
                    resumeStepIndex = i;
                    break;
                  }
                }
                continue;
              } else {
                resumeStepIndex = i;
                break;
              }
            } catch (error) {
              console.error('Error loading substeps for container step:', error);
            }
          } else {
            const stepExec = existingSteps.find(se => se.stepId === step.stepId);
            if (!stepExec || stepExec.status === StepExecutionStatus.PENDING || stepExec.status === StepExecutionStatus.IN_PROGRESS) {
              resumeStepIndex = i;
              break;
            }
          }
        }
        setCurrentStepIndex(resumeStepIndex);
      } else {
        const topLevelSteps = steps
          .filter(step => !isSubstep(step, steps))
          .sort((a, b) => a.order - b.order);

        const createdSteps: StepExecution[] = [];
        for (let i = 0; i < topLevelSteps.length; i++) {
          const step = topLevelSteps[i];
          if (step.stepType === StepType.SWITCH || step.stepType === StepType.REPEAT) {
            continue;
          }
          const stepResponse: createStepExecutionRequest = await graphQlClient.request(
            ProcessExecutionGraphQL.mutations.createStepExecution,
            {
              input: {
                processExecutionId: executionId,
                stepId: step.stepId,
                sequenceOrder: i,
                stepSnapshot: {
                  name: step.name,
                  stepType: step.stepType,
                  notes: step.notes,
                  stepData: step.stepData,
                  order: step.order,
                },
              },
            },
          );
          createdSteps.push(stepResponse.createStepExecution);
        }
        setStepExecutions(createdSteps);
        setCurrentStepIndex(0);
      }

      setIsExecuting(true);
    } catch (error) {
      console.error('Error starting execution:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [currentProcess, processId, pendingExecutionId, dispatch, localeProcesses, localeActionMessages, isSubstep, sortStepExecutions]);

  // Check for existing execution and show confirmation modal if needed
  const checkAndShowExecutionConfirm = useCallback(async () => {
    try {
      const response: getMyPendingExecutionsRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyPendingExecutions,
        { pagination: { limit: 50, offset: 0 } },
      );

      const myExecutions = response.getMyPendingExecutions?.items || [];
      const inProgressExecution = myExecutions.find(
        (exec) => exec.processId === processId && exec.status === 'IN_PROGRESS'
      );
      const assignedExecution = myExecutions.find(
        (exec) => exec.processId === processId && exec.status === 'ASSIGNED'
      );
      // If there's an in-progress execution, show continue modal first
      // The assigner note will be shown after choosing to continue
      if (inProgressExecution) {
        setPendingExecutionStartDate(inProgressExecution.startedAt || null);
        setPendingExecutionId(inProgressExecution.processExecutionId);
        if (inProgressExecution.assignerNote) {
          setAssignerNoteContent(inProgressExecution.assignerNote);
        }
        setShowContinueExecutionModal(true);
        return;
      }

      // For assigned executions (not yet started), show note first if exists
      if (assignedExecution?.assignerNote) {
        setAssignerNoteContent(assignedExecution.assignerNote);
        setNoteModalNextAction('start');
        setShowAssignerNoteModal(true);
        return;
      }

      // Start execution directly
      handleStartExecutionInternal();
    } catch (error) {
      console.error('Error checking execution status:', error);
      handleStartExecutionInternal();
    }
  }, [processId, handleStartExecutionInternal]);

  // Public start execution (wrapper)
  const handleStartExecution = useCallback(async (forceNew: boolean = false) => {
    await handleStartExecutionInternal(forceNew);
  }, [handleStartExecutionInternal]);

  // Mark step
  const handleMarkStep = useCallback(async (stepIndex: number, status: StepExecutionStatus, notes?: string, confirmationResponse?: string) => {
    if (!processExecutionId) return;

    const filteredSteps = currentProcess?.steps
      ?.filter(step => !isSubstep(step, currentProcess.steps || []))
      .sort((a, b) => a.order - b.order) || [];
    const originalStep = filteredSteps[stepIndex];

    if (!originalStep) return;

    if (originalStep.stepType === StepType.SWITCH || originalStep.stepType === StepType.REPEAT) {
      console.warn('handleMarkStep called for container step - this should not happen');
      return;
    }

    let currentStepExecution = stepExecutions.find(se => se.stepId === originalStep.stepId);

    if (status === StepExecutionStatus.COMPLETED && originalStep?.stepData?.confirmationType) {
      if (originalStep.stepData.confirmationType === ConfirmationType.BOOLEAN) {
        if (confirmationResponse !== 'true') {
          dispatch(appendActionMessage({
            message: localeProcesses['confirmationCheckRequired'],
            type: SnackType.ERROR,
          }));
          return;
        }
      } else if (originalStep.stepData.confirmationType === ConfirmationType.CUSTOM_TEXT) {
        if (!confirmationResponse || confirmationResponse.trim() === '') {
          dispatch(appendActionMessage({
            message: localeProcesses['confirmationTextRequired'],
            type: SnackType.ERROR,
          }));
          return;
        }
      }
    }

    try {
      if (!currentStepExecution) {
        const createResponse: createStepExecutionRequest = await graphQlClient.request(
          ProcessExecutionGraphQL.mutations.createStepExecution,
          {
            input: {
              processExecutionId: processExecutionId,
              stepId: originalStep.stepId,
              sequenceOrder: stepIndex,
              stepSnapshot: {
                name: originalStep.name,
                stepType: originalStep.stepType,
                notes: originalStep.notes,
                stepData: originalStep.stepData,
                order: originalStep.order,
              },
            },
          },
        );
        currentStepExecution = createResponse.createStepExecution;
      }

      const updateInput: any = {
        stepExecutionId: currentStepExecution.stepExecutionId,
        status: status,
      };

      if (notes) {
        updateInput.notes = notes;
        if (status === StepExecutionStatus.FAILED) {
          updateInput.errorDetails = { message: notes };
        }
      }

      if (confirmationResponse && status === StepExecutionStatus.COMPLETED) {
        updateInput.confirmationResponse = confirmationResponse;
      }

      const response: updateStepExecutionRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.updateStepExecution,
        { input: updateInput },
      );

      let updatedSteps = [...stepExecutions];
      const existingIndex = updatedSteps.findIndex(se => se.stepId === originalStep.stepId);
      if (existingIndex >= 0) {
        updatedSteps[existingIndex] = response.updateStepExecution;
      } else {
        updatedSteps.push(response.updateStepExecution);
        updatedSteps.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
      }
      setStepExecutions(updatedSteps);

      const stepsNeedingExecution: number[] = [];
      for (let i = 0; i < filteredSteps.length; i++) {
        const step = filteredSteps[i];
        if (step.stepType === StepType.SWITCH || step.stepType === StepType.REPEAT) {
          const childStepIds = step.stepData?.stepsIds
            ?.split(',')
            .filter((id: string) => id && id.trim() && id !== 'undefined') || [];

          if (childStepIds.length === 0) continue;

          const substeps = substepExecutions.get(step.stepId) || [];
          if (substeps.length === 0) {
            stepsNeedingExecution.push(i);
          } else {
            const allSubstepsComplete = substeps.every(s =>
              s.status === StepExecutionStatus.COMPLETED ||
              s.status === StepExecutionStatus.SKIPPED ||
              s.status === StepExecutionStatus.FAILED
            );
            if (!allSubstepsComplete) {
              stepsNeedingExecution.push(i);
            }
          }
        } else {
          const execution = updatedSteps.find(se => se.stepId === step.stepId);
          if (!execution || execution.status === StepExecutionStatus.PENDING) {
            stepsNeedingExecution.push(i);
          }
        }
      }

      let nextPendingIndex = stepsNeedingExecution.find(idx => idx > stepIndex);
      if (nextPendingIndex === undefined) {
        nextPendingIndex = stepsNeedingExecution[0];
      }

      if (nextPendingIndex !== undefined) {
        setCurrentStepIndex(nextPendingIndex);

        const nextStep = filteredSteps[nextPendingIndex];
        if (nextStep?.stepId && stepsRefs.current[nextStep.stepId]) {
          setTimeout(() => {
            if (nextStep?.stepId && stepsRefs.current[nextStep.stepId]) {
              stepsRefs.current[nextStep.stepId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      } else {
        const hasFailedSteps = updatedSteps.some((step) => step.status === StepExecutionStatus.FAILED);
        const hasSkippedSteps = updatedSteps.some((step) => step.status === StepExecutionStatus.SKIPPED);

        if (hasFailedSteps || hasSkippedSteps) {
          setShowStatusModal(true);
        } else {
          await handleCompleteExecution(ProcessExecutionStatus.COMPLETED);
        }
      }
    } catch (error) {
      console.error('Error updating step:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [processExecutionId, currentProcess?.steps, stepExecutions, substepExecutions, isSubstep, dispatch, localeProcesses, localeActionMessages, stepsRefs, handleCompleteExecution]);

  // Cancel execution
  const handleCancelExecution = useCallback(async () => {
    if (processExecutionId && isExecuting) {
      try {
        await graphQlClient.request(ProcessExecutionGraphQL.mutations.completeProcessExecution, {
          id: processExecutionId,
          status: ProcessExecutionStatus.CANCELLED,
        });

        dispatch(appendActionMessage({
          message: localeProcesses['executionCancelled'],
          type: SnackType.WARNING,
        }));
      } catch (error) {
        console.error('Error cancelling execution:', error);
      }
    }

    resetExecutionState();

    if (processHeaderRef.current) {
      await processHeaderRef.current.fetchLastExecution();
    }

    await refreshPendingExecutionsCount();
  }, [processExecutionId, isExecuting, dispatch, localeProcesses, resetExecutionState, processHeaderRef, refreshPendingExecutionsCount]);

  // Go back
  const handleGoBack = useCallback(async () => {
    if (!processExecutionId || currentStepIndex === 0) return;

    setIsGoingBack(true);

    try {
      const response: goBackToPreviousStepRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.goBackToPreviousStep,
        { processExecutionId },
      );

      if (response.goBackToPreviousStep) {
        const deletedStepId = response.goBackToPreviousStep.stepExecutionId;
        const updatedSteps = stepExecutions.filter(step =>
          step.stepExecutionId !== deletedStepId
        );
        setStepExecutions(updatedSteps);

        const filteredSteps = currentProcess?.steps
          ?.filter(step => !isSubstep(step, currentProcess.steps || []))
          .sort((a, b) => a.order - b.order) || [];
        const previousStep = filteredSteps[currentStepIndex - 1];

        if (previousStep?.stepType === StepType.REPEAT) {
          setSubstepExecutions(prev => {
            const newMap = new Map(prev);
            newMap.delete(previousStep.stepId);
            return newMap;
          });
          setRepeatIterations(prev => {
            const newMap = new Map(prev);
            newMap.delete(previousStep.stepId);
            return newMap;
          });
          setCurrentSubstepIndices(prev => {
            const newMap = new Map(prev);
            newMap.delete(previousStep.stepId);
            return newMap;
          });
          setShowIterationCompleteFor(null);
        }

        setCurrentStepIndex(currentStepIndex - 1);

        dispatch(appendActionMessage({
          message: localeProcesses['goBackStepSuccess'],
          type: SnackType.SUCCESS,
        }));
      } else {
        dispatch(appendActionMessage({
          message: localeProcesses['goBackStepNoPrevious'],
          type: SnackType.INFO,
        }));
      }
    } catch (error) {
      console.error('Error going back to previous step:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    } finally {
      setIsGoingBack(false);
    }
  }, [processExecutionId, currentStepIndex, stepExecutions, currentProcess?.steps, isSubstep, dispatch, localeProcesses, localeActionMessages]);

  // Mark substep
  const handleMarkSubstep = useCallback(async (
    parentStepId: string,
    substepIndex: number,
    status: StepExecutionStatus,
    notes?: string,
    confirmationResponse?: string
  ) => {
    const substeps = getSubstepsForParent(parentStepId);
    const currentIteration = repeatIterations.get(parentStepId) || 1;
    const parentStep = currentProcess?.steps?.find(s => s.stepId === parentStepId);

    const relevantSubsteps = parentStep?.stepType === StepType.REPEAT
      ? substeps.filter(s => s.iterationNumber === currentIteration).sort((a, b) => (a.substepOrder ?? 0) - (b.substepOrder ?? 0))
      : [...substeps].sort((a, b) => (a.substepOrder ?? 0) - (b.substepOrder ?? 0));

    const currentSubstep = relevantSubsteps[substepIndex];
    if (!currentSubstep) return;

    try {
      const updateInput: any = {
        stepExecutionId: currentSubstep.stepExecutionId,
        status: status,
      };
      if (notes) {
        updateInput.notes = notes;
        if (status === StepExecutionStatus.FAILED) {
          updateInput.errorDetails = { message: notes };
        }
      }
      if (confirmationResponse && status === StepExecutionStatus.COMPLETED) {
        updateInput.confirmationResponse = confirmationResponse;
      }

      const response: updateStepExecutionRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.mutations.updateStepExecution,
        { input: updateInput }
      );

      const allSubsteps = [...substeps];
      const globalIndex = allSubsteps.findIndex(s => s.stepExecutionId === currentSubstep.stepExecutionId);
      if (globalIndex >= 0) {
        allSubsteps[globalIndex] = response.updateStepExecution;
      }
      setSubstepExecutions(prev => new Map(prev).set(parentStepId, allSubsteps));

      const updatedRelevantSubsteps = parentStep?.stepType === StepType.REPEAT
        ? allSubsteps.filter(s => s.iterationNumber === currentIteration)
        : allSubsteps;

      const allCompleted = updatedRelevantSubsteps.every(s =>
        s.status === StepExecutionStatus.COMPLETED ||
        s.status === StepExecutionStatus.SKIPPED ||
        s.status === StepExecutionStatus.FAILED
      );

      if (allCompleted) {
        if (parentStep?.stepType === StepType.SWITCH) {
          moveToNextMainStep(parentStepId, allSubsteps);
        } else if (parentStep?.stepType === StepType.REPEAT) {
          setShowIterationCompleteFor(parentStepId);
        }
      } else {
        const nextSubstepIndex = substepIndex + 1;
        setCurrentSubstepIndices(prev => new Map(prev).set(parentStepId, nextSubstepIndex));

        setTimeout(() => {
          const nextSubstep = document.querySelector(`[data-substep-parent="${parentStepId}"][data-substep-index="${nextSubstepIndex}"]`);
          nextSubstep?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    } catch (error) {
      console.error('Error updating substep:', error);
      dispatch(appendActionMessage({
        message: localeActionMessages['error'],
        type: SnackType.ERROR,
      }));
    }
  }, [getSubstepsForParent, repeatIterations, currentProcess?.steps, moveToNextMainStep, dispatch, localeActionMessages]);

  // Handle repeat add another
  const handleRepeatAddAnother = useCallback(async (parentStepId: string) => {
    const currentIteration = repeatIterations.get(parentStepId) || 1;

    setCompletedIterations(prev => {
      const existing = prev.get(parentStepId) || [];
      return new Map(prev).set(parentStepId, [...existing, currentIteration]);
    });

    const filteredSteps = currentProcess?.steps
      ?.filter(step => !isSubstep(step, currentProcess.steps || []))
      .sort((a, b) => a.order - b.order) || [];
    const stepIndex = filteredSteps.findIndex(s => s.stepId === parentStepId);

    await startRepeatIteration(parentStepId, stepIndex, currentIteration + 1);

    setTimeout(() => {
      stepsRefs.current[parentStepId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [repeatIterations, currentProcess?.steps, isSubstep, startRepeatIteration, stepsRefs]);

  // Handle repeat done
  const handleRepeatDone = useCallback(async (parentStepId: string) => {
    const currentSubsteps = getSubstepsForParent(parentStepId);
    await moveToNextMainStep(parentStepId, currentSubsteps);
  }, [getSubstepsForParent, moveToNextMainStep]);

  // Auto-initialize substeps when landing on a SWITCH/REPEAT step during execution
  useEffect(() => {
    if (!isExecuting || !currentProcess?.steps || !processExecutionId) return;

    const filteredSteps = currentProcess.steps
      .filter(step => !isSubstep(step, currentProcess.steps || []))
      .sort((a, b) => a.order - b.order);

    const currentStep = filteredSteps[currentStepIndex];
    if (!currentStep) return;

    if (!isContainerStep(currentStep)) return;

    const existingSubsteps = substepExecutions.get(currentStep.stepId);
    if (existingSubsteps && existingSubsteps.length > 0) return;

    const childStepIds = currentStep.stepData?.stepsIds
      ?.split(',')
      .filter((id: string) => id && id.trim() && id !== 'undefined') || [];

    if (childStepIds.length === 0) {
      moveToNextMainStep(currentStep.stepId);
      return;
    }

    if (currentStep.stepType === StepType.SWITCH) {
      initializeSwitchExecution(currentStep.stepId, currentStepIndex);
    } else if (currentStep.stepType === StepType.REPEAT) {
      startRepeatIteration(currentStep.stepId, currentStepIndex, 1);
    }
  }, [currentStepIndex, isExecuting, substepExecutions, currentProcess?.steps, processExecutionId, isSubstep, isContainerStep, moveToNextMainStep, initializeSwitchExecution, startRepeatIteration]);

  return {
    // State
    isExecuting,
    processExecutionId,
    stepExecutions,
    currentStepIndex,
    showStatusModal,
    isGoingBack,
    substepExecutions,
    currentSubstepIndices,
    repeatIterations,
    completedIterations,
    showIterationCompleteFor,
    showContinueExecutionModal,
    pendingExecutionStartDate,
    pendingExecutionId,
    showAssignerNoteModal,
    assignerNoteContent,
    noteModalNextAction,

    // State setters
    setShowStatusModal,
    setShowContinueExecutionModal,
    setShowAssignerNoteModal,
    setAssignerNoteContent,
    setPendingExecutionStartDate,
    setPendingExecutionId,
    setNoteModalNextAction,

    // Functions
    isSubstep,
    checkAndShowExecutionConfirm,
    handleStartExecution,
    handleMarkStep,
    handleCompleteExecution,
    handleCancelExecution,
    handleGoBack,
    getSubstepsForParent,
    isContainerStep,
    initializeSwitchExecution,
    startRepeatIteration,
    handleMarkSubstep,
    handleRepeatAddAnother,
    handleRepeatDone,
  };
};
